import { SignalDoc, SignalLevel, SignalSubjectDocType } from "@megaron/docs-contracts";

import { getSignalDisplayName } from "./getSignalDisplayName";
import { signalDisplayLabel } from "./signalDisplayLabel";
import { signalLevelLabel } from "./signalLevel";
import { signalSubjectDocTypeLabel } from "./signalSubjectDocTypeLabel";

export type SignalsSortField = "createdAt" | "name" | "level" | "app";

export type SignalsSortValue = {
  field: SignalsSortField;
  order: "ASC" | "DESC";
};

export type SignalsFilterValues = {
  searchText: string;
  apps: string[];
  levels: SignalLevel[];
  signalTypes: string[];
  subjectDocTypes: SignalSubjectDocType[];
  isUnread: boolean | undefined;
  sort: SignalsSortValue | undefined;
};

export const defaultSignalsFilters: SignalsFilterValues = {
  searchText: "",
  apps: [],
  levels: [],
  signalTypes: [],
  subjectDocTypes: [],
  isUnread: true,
  sort: { field: "createdAt", order: "DESC" },
};

const matchesSearch = (
  signal: SignalDoc,
  searchText: string,
  appLabel: (app: string) => string,
): boolean => {
  const query = searchText.trim().toLowerCase();
  if (!query) return true;

  const haystack = [
    signalDisplayLabel(signal),
    getSignalDisplayName(signal),
    appLabel(signal.app),
    signalLevelLabel[signal.level],
    signal.signalType,
    signalSubjectDocTypeLabel[signal.subjectDocType],
    signal.subjectDocId,
  ]
    .join(" ")
    .toLowerCase();

  return haystack.includes(query);
};

export const filterSignals = (
  signals: SignalDoc[],
  filters: SignalsFilterValues,
  appLabel: (app: string) => string,
): SignalDoc[] =>
  signals.filter((signal) => {
    if (filters.apps.length > 0 && !filters.apps.includes(signal.app)) return false;
    if (filters.levels.length > 0 && !filters.levels.includes(signal.level)) return false;
    if (filters.signalTypes.length > 0 && !filters.signalTypes.includes(signal.signalType)) return false;
    if (filters.subjectDocTypes.length > 0 && !filters.subjectDocTypes.includes(signal.subjectDocType)) return false;
    return matchesSearch(signal, filters.searchText, appLabel);
  });

export const isSignalsSortValue = (value: unknown): value is SignalsSortValue =>
  value !== null &&
  typeof value === "object" &&
  "field" in value &&
  "order" in value &&
  (value.field === "createdAt" ||
    value.field === "name" ||
    value.field === "level" ||
    value.field === "app") &&
  (value.order === "ASC" || value.order === "DESC");
